import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { PERFORMANCE_CONFIG } from '../utils/performance';

const NFTSlideshow: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imageLoadErrors, setImageLoadErrors] = useState<Set<number>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [visibleThumbnails, setVisibleThumbnails] = useState<Set<number>>(new Set());
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Function to shuffle array using Fisher-Yates algorithm
  const shuffleArray = (array: string[]) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Dynamic image loading with diverse variety across all collections - OPTIMIZED
  const loadAvailableImages = () => {
    const collections = {
      genesis: 330,
      freedom: 360,
      keepitreal: 243,
    };

    const allImages: string[] = [];

    // Load only a subset of images for better performance
    for (const [collection, count] of Object.entries(collections)) {
      const maxImages = Math.min(PERFORMANCE_CONFIG.MAX_IMAGES_PER_COLLECTION, count);
      const step = Math.max(1, Math.floor(count / maxImages)); // Ensure step is at least 1
      
      for (let i = 1; i <= maxImages; i++) {
        const imageIndex = Math.min(i * step, count);
        // Ensure imageIndex is never 0 (since images start at preview1.webp)
        const validIndex = Math.max(1, imageIndex);
        allImages.push(`/images/collections/${collection}/preview${validIndex}.webp`);
      }
    }
    
    return allImages;
  };

  // Get available images - memoized so it only loads once when component mounts
  const nftImages = useMemo(() => {
    const images = loadAvailableImages();
    console.log('Key Images loaded (optimized):', images.length, 'images'); // Debug: show count
    console.log('Sample image paths:', images.slice(0, 5)); // Debug: show sample paths
    
    // Validate that all paths are reasonable
    const invalidPaths = images.filter(path => {
      const match = path.match(/preview(\d+)\.webp$/);
      return !match || parseInt(match[1]) < 1;
    });
    
    if (invalidPaths.length > 0) {
      console.error('Invalid image paths detected:', invalidPaths);
    }
    
    return shuffleArray(images);
  }, []);

  const nextSlide = () => {
    const nextIndex = (currentIndex + 1) % nftImages.length;
    changeSlide(nextIndex);
  };

  const prevSlide = () => {
    const prevIndex = (currentIndex - 1 + nftImages.length) % nftImages.length;
    changeSlide(prevIndex);
  };

  // State for managing transitions
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Function to change slide with fade effect
  const changeSlide = useCallback((newIndex: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    
    // Fade out
    setTimeout(() => {
      setCurrentIndex(newIndex);
      setIsTransitioning(false);
    }, 500); // Half of transition duration
  }, [isTransitioning]);

  // Intersection Observer for lazy loading thumbnails
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setVisibleThumbnails(prev => new Set(prev).add(index));
          }
        });
      },
      { rootMargin: '50px' }
    );

    thumbnailRefs.current.forEach((ref, index) => {
      if (ref) {
        ref.setAttribute('data-index', index.toString());
        observer.observe(ref);
      }
    });

    return () => observer.disconnect();
  }, [nftImages.length]);

  // Auto-play functionality
  useEffect(() => {
    const interval = setInterval(() => {
      const nextIndex = (currentIndex + 1) % nftImages.length;
      changeSlide(nextIndex);
    }, PERFORMANCE_CONFIG.SLIDESHOW_INTERVAL); // Change slide every 3 seconds

    return () => clearInterval(interval); // Cleanup on unmount
  }, [currentIndex, nftImages.length, changeSlide]);

  // Optimized preloading - only preload next image and limit frequency
  useEffect(() => {
    if (nftImages.length > 0) {
      const nextIndex = (currentIndex + 1) % nftImages.length;

      // Only preload next image (not previous) to reduce memory usage
      const nextImage = new Image();
      nextImage.src = nftImages[nextIndex];
      
      // Clean up previous preloaded images to prevent memory leaks
      return () => {
        nextImage.src = '';
      };
    }
  }, [currentIndex, nftImages]);

  // Handle image load errors
  const handleImageError = (index: number) => {
    console.log('Image load error for index:', index, 'URL:', nftImages[index]); // Debug
    setImageLoadErrors(prev => new Set(Array.from(prev).concat(index)));
  };

  // Handle image load success
  const handleImageLoad = () => {
    setIsLoading(false);
  };

  // Check if current image has error
  const currentImageHasError = imageLoadErrors.has(currentIndex);

  const scrollThumbnails = (direction: 'left' | 'right') => {
    if (thumbnailContainerRef.current) {
      const scrollAmount = direction === 'left' ? -400 : 400;
      thumbnailContainerRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="relative w-full overflow-hidden">
        {/* Loading State */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-900" style={{ minHeight: '700px' }}>
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-400">Loading Key Collection...</p>
            </div>
          </div>
        )}

        {/* Main Image Display - Optimized for 1920x1080 landscape */}
        <div className="relative w-full flex items-center justify-center" style={{ 
          minHeight: '540px', 
          maxHeight: '80vh',
          aspectRatio: '16/9' // Perfect for 1920x1080 images
        }}>
          {currentImageHasError ? (
            // Placeholder for missing images
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-900 to-purple-900 text-gray-300">
              <div className="text-center p-8">
                <div className="w-24 h-24 mx-auto mb-4 bg-blue-800 rounded-full flex items-center justify-center">
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2">New Collection Coming Soon</h3>
                <p className="text-blue-300">Updated Key artwork will be available here</p>
              </div>
            </div>
          ) : (
            <img
              src={nftImages[currentIndex]}
              alt={`Key Artwork ${currentIndex + 1}`}
              className={`w-full h-full object-cover transition-opacity duration-500 rounded-lg ${
                isTransitioning ? 'opacity-0' : 'opacity-100'
              }`}
              style={{
                aspectRatio: '16/9', // Maintain 1920x1080 aspect ratio
                maxWidth: '100%',
                maxHeight: '100%'
              }}
              onError={() => {
                console.warn('Failed to load main image:', nftImages[currentIndex]);
                handleImageError(currentIndex);
              }}
              onLoad={handleImageLoad}
              loading="eager"
            />
          )}
        
        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-gray-300 p-3 rounded-full transition-all duration-200 z-10"
          aria-label="Previous image"
        >
          <ChevronLeftIcon className="h-6 w-6" />
        </button>
        
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-gray-300 p-3 rounded-full transition-all duration-200 z-10"
          aria-label="Next image"
        >
          <ChevronRightIcon className="h-6 w-6" />
        </button>

        {/* Image Counter */}
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-gray-300 px-4 py-2 rounded-full text-sm">
          {currentIndex + 1} / {nftImages.length}
        </div>
        </div>
      </div>

      {/* Thumbnail Row with Navigation - Virtualized for Performance */}
      <div className="relative px-8">
        <div 
          ref={thumbnailContainerRef}
          className="flex gap-3 overflow-x-auto overflow-y-hidden py-3 px-2 scroll-smooth hide-scrollbar"
        >
          {nftImages.map((image, index) => {
            // Proper lazy loading like app.bornfree.io - only render visible thumbnails
            const isCurrentSlide = index === currentIndex;
            const isInBuffer = Math.abs(index - currentIndex) <= PERFORMANCE_CONFIG.THUMBNAIL_BUFFER_SIZE;
            const shouldRender = isCurrentSlide || isInBuffer;
            
            return (
              <div
                key={index}
                ref={(el) => (thumbnailRefs.current[index] = el)}
                onClick={() => changeSlide(index)}
                className={`flex-shrink-0 w-36 h-20 rounded-md overflow-hidden cursor-pointer transition-all duration-200 ease-in-out
                  ${currentIndex === index ? 'border-4 border-cyan-500 opacity-100 shadow-lg shadow-cyan-500/30' : 'border-4 border-transparent opacity-75 hover:opacity-90 hover:scale-105'}
                `}
              >
                {(shouldRender || visibleThumbnails.has(index)) ? (
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      console.error('Failed to load thumbnail:', image);
                      handleImageError(index);
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                    <div className="w-6 h-6 bg-gray-600 rounded animate-pulse"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        
        {/* Thumbnail Navigation Buttons */}
        <button
          onClick={() => scrollThumbnails('left')}
          className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/80 text-gray-700 border border-gray-300 rounded-full w-7 h-7 flex items-center justify-center cursor-pointer z-10 shadow-sm hover:bg-white"
          aria-label="Scroll thumbnails left"
        >
          <ChevronLeftIcon className="w-4 h-4" />
        </button>
        <button
          onClick={() => scrollThumbnails('right')}
          className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/80 text-gray-700 border border-gray-300 rounded-full w-7 h-7 flex items-center justify-center cursor-pointer z-10 shadow-sm hover:bg-white"
          aria-label="Scroll thumbnails right"
        >
          <ChevronRightIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default NFTSlideshow; 